/**
 * Portal Location Context
 *
 * Manages location state for multi-location retail partners in the Customer Portal.
 * Provides location switching capability and location-aware data filtering.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import portalAPI from '../services/portalAPI';

interface RetailLocation {
  id: number;
  name: string;
  short_name: string;
  full_display_name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
  email: string;
  member_count: number;
  is_active: boolean;
  bridge_club_enabled: boolean;
}

interface RetailOrg {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  bridge_club_enabled: boolean;
  location_count: number;
  total_members: number;
}

interface PortalLocationContextType {
  // Data
  retailOrg: RetailOrg | null;
  locations: RetailLocation[];
  selectedLocation: RetailLocation | null;

  // Computed
  isMultiLocation: boolean;
  isOrgOwner: boolean;

  // Actions
  switchLocation: (locationId: number | null) => Promise<void>;
  refreshLocations: () => Promise<void>;

  // State
  loading: boolean;
  error: string | null;

  // Helper to get location display name
  getLocationDisplayName: () => string;
}

const PortalLocationContext = createContext<PortalLocationContextType | null>(null);

export const PortalLocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [retailOrg, setRetailOrg] = useState<RetailOrg | null>(null);
  const [locations, setLocations] = useState<RetailLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<RetailLocation | null>(null);
  const [isOrgOwner, setIsOrgOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLocations = useCallback(async () => {
    // Only load on portal routes
    const path = window.location.pathname;
    if (!path.startsWith('/portal')) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await portalAPI.get('/portal/my-locations/');

      setRetailOrg(response.data.retail_org);
      setLocations(response.data.locations || []);
      setIsOrgOwner(response.data.is_org_owner || false);

      // Set initial selected location
      const savedLocationId = sessionStorage.getItem('portal_selected_location_id');
      if (savedLocationId && response.data.locations?.length > 1) {
        const saved = response.data.locations.find(
          (l: RetailLocation) => l.id === parseInt(savedLocationId)
        );
        if (saved) {
          setSelectedLocation(saved);
        }
      } else if (response.data.locations?.length === 1) {
        // Single location - auto-select it
        setSelectedLocation(response.data.locations[0]);
      }
      // Multi-location owners start with "All" (null) unless they have a saved preference

    } catch (err: any) {
      // Don't treat 403/404 as errors - user may not be a retail partner
      if (err.response?.status === 403 || err.response?.status === 404) {
        // Not a multi-location user, that's fine
        setLocations([]);
        setRetailOrg(null);
      } else {
        console.error('Failed to load locations:', err);
        setError(err.message || 'Failed to load locations');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const switchLocation = async (locationId: number | null) => {
    try {
      // Update backend session
      await portalAPI.post('/portal/switch-location/', { location_id: locationId });

      // Update local state
      if (locationId === null) {
        setSelectedLocation(null);
        sessionStorage.removeItem('portal_selected_location_id');
      } else {
        const location = locations.find(l => l.id === locationId);
        setSelectedLocation(location || null);
        if (location) {
          sessionStorage.setItem('portal_selected_location_id', locationId.toString());
        }
      }
    } catch (err) {
      console.error('Failed to switch location:', err);
      throw err;
    }
  };

  const getLocationDisplayName = useCallback(() => {
    if (selectedLocation) {
      return selectedLocation.full_display_name || selectedLocation.name;
    }
    if (locations.length > 1) {
      return 'All Locations';
    }
    return retailOrg?.name || 'Store';
  }, [selectedLocation, locations.length, retailOrg?.name]);

  const value: PortalLocationContextType = {
    retailOrg,
    locations,
    selectedLocation,
    isMultiLocation: locations.length > 1,
    isOrgOwner,
    switchLocation,
    refreshLocations: loadLocations,
    loading,
    error,
    getLocationDisplayName,
  };

  return (
    <PortalLocationContext.Provider value={value}>
      {children}
    </PortalLocationContext.Provider>
  );
};

export const usePortalLocation = () => {
  const context = useContext(PortalLocationContext);
  if (!context) {
    throw new Error('usePortalLocation must be used within PortalLocationProvider');
  }
  return context;
};

// Hook that returns null if not within provider (safe for optional usage)
export const usePortalLocationOptional = () => {
  return useContext(PortalLocationContext);
};

export default PortalLocationContext;
